import { CommodityInfo } from "../interfaces/CommodityInfo";
import { Option } from "../interfaces/Option";
import { apiGet } from "./api";
import Logger from "../utils/Logger";

interface CommodityResponse {
  responseObject: CommodityInfo[];
}

const fetchCommodity = async (): Promise<CommodityInfo[]> => {
  const mockResult: CommodityInfo[] = [
    { id: 1, name: "electronics" },
    { id: 2, name: "3tg" },
  ];

  try {
    Logger.info("Fetching commodity data from API", ["CommodityApi"]);
    sessionStorage.removeItem("commodityArrayListStored");

    const commodityResult = await apiGet<CommodityResponse>(
      `/lookup?type=commodities`,
    );

    const result: CommodityInfo[] = commodityResult.responseObject;
    if (result) {
      Logger.info(`Successfully fetched ${result.length} commodities`, [
        "CommodityApi",
      ]);
      return result;
    }

    Logger.warn("CommodityApi Response without response object", [
      "CommodityApi",
    ]);
    return [];
  } catch (err) {
    Logger.error("Error fetching commodity data from remote API", err, [
      "CommodityApi",
    ]);
    Logger.warn("Using mock data for commodity list", ["CommodityApi"]);
    return mockResult;
  }
};

const commodityFromRemote = async (): Promise<Option[]> => {
  const response = await fetchCommodity();
  Logger.debug("Processing commodity response", ["CommodityApi"]);

  if (!Array.isArray(response)) {
    Logger.error(
      "Expected responseObject to be an array, but received:",
      response,
      ["CommodityApi"],
    );
    return []; // Return an empty array to prevent runtime errors
  }

  const commodityArrayList: Option[] = response
    .map((item: CommodityInfo) => ({
      label: item.name?.toUpperCase() || "", // Use empty string as a fallback
      value: item.name || "",
    }))
    .filter((item) => item.label && item.value) // Filter out invalid items
    .sort((a, b) => a.value.localeCompare(b.value)); // Use localeCompare for simpler string sorting;

  Logger.info(
    `Processed ${commodityArrayList.length} valid commodity options`,
    ["CommodityApi"],
  );

  if (commodityArrayList.length > 2) {
    sessionStorage.setItem(
      "commodityArrayListStored",
      JSON.stringify(commodityArrayList),
    );
    Logger.debug("Cached commodity list in session storage", ["CommodityApi"]);
  }

  return commodityArrayList;
};

const getCommodityList = async () => {
  const storedCommodityArrayList = sessionStorage.getItem(
    "commodityArrayListStored",
  );

  if (storedCommodityArrayList) {
    Logger.debug("Loading commodity list from cache", ["CommodityApi"]);
    return JSON.parse(storedCommodityArrayList) as Option[];
  } else {
    Logger.debug("No cached commodity list found, fetching from remote", [
      "CommodityApi",
    ]);
    return await commodityFromRemote();
  }
};

export const CommodityApi = {
  getCommodityList,
};
